import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import loveCodes from '@autopass-web/domain/json/love_code.json';
import { MochiButton } from '@autopass-web/mochi-component';

import { useImperativeForm } from '../../../../hooks/use-bind-async-submit';
import { emailSchema } from '../../utils';
import { EmailInput } from '../UI';
import styles from './donate.module.scss';
import { DonateListPanel } from './donate-list-panel';

import type { ReceiptInfoBase } from '../../../../configs/receipts';
import type { EmailFieldConfig, ReceiptCompProps } from '../../utils';

function getDonateName(code: string) {
  const codeObj = loveCodes.find((obj) => obj.love_code === code);
  return codeObj?.name || '';
}

export interface DonateReceiptInfo extends ReceiptInfoBase {
  type: 'donate';
  love_code: string;
  email: string;
}

type DonateReceiptForm = Omit<DonateReceiptInfo, 'type' | 'love_code'>;

type CarrierMemberReceiptProps = ReceiptCompProps<{
  emailFieldConfig: EmailFieldConfig;
}>;

export const DonateReceipt = ({
  controlRef,
  emailFieldConfig,
  storage,
  formMode,
}: CarrierMemberReceiptProps) => {
  const schema =
    emailFieldConfig === 'required'
      ? z.object({ email: emailSchema })
      : emailFieldConfig === 'default'
        ? z.object({ email: emailSchema.or(z.string().length(0)) })
        : z.object({});

  const [showPanel, setShowPanel] = useState(false);
  const [loveCode, setLoveCode] = useState<string>(() => {
    const { love_code = '88888' } = storage?.get?.() ?? {};

    return love_code;
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    subscribe,
  } = useForm<DonateReceiptForm>({
    defaultValues: { ...(storage?.get?.() ?? {}) },
    mode: formMode ?? 'onSubmit',
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    const cb = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        const receiptInfo = storage?.get?.();

        storage?.set({ ...receiptInfo, ...values, type: 'donate' });
      },
    });

    return () => cb();
  }, [subscribe]);

  const formProps = useImperativeForm(controlRef, errors, (submit) => {
    return handleSubmit((data) =>
      submit({ ...data, type: 'donate', love_code: loveCode }),
    );
  });

  return (
    <>
      <form {...formProps}>
        <div className={styles['donate-info']}>
          <div className={styles['text']}>
            <span>愛心碼 {loveCode}</span>
            <div className={styles['desc']}>{getDonateName(loveCode)}</div>
          </div>
          <MochiButton
            type="button"
            className={styles['button']}
            variant="text"
            size="large"
            onClick={() => {
              setShowPanel(true);
            }}
          >
            變更
          </MochiButton>
        </div>
        <EmailInput
          config={emailFieldConfig}
          errorMessage={errors.email?.message}
          {...register('email')}
        />
      </form>
      <DonateListPanel
        isOpen={showPanel}
        onClose={() => {
          setShowPanel(false);
        }}
        onLoveCodeChange={(value) => {
          setLoveCode(value);
          const info = storage?.get?.();
          storage?.set({ ...info, love_code: value, type: 'donate' });
          setShowPanel(false);
        }}
        loveCode={loveCode}
      />
    </>
  );
};
